import React, { createContext, useContext } from "react";
import { useLogger } from "./Logger";
import { useSessionId } from "./SessionId";
import { AnalyticsBuilders } from "@investec/cadi-core";

const defaultAnalytics = {
    /**
     * @param {string} eventName
     * @param {string} featureName
     */
    trackAnalyticsPageEvent: (eventName, featureName) => {},

    /**
     * @param {string} eventName
     * @param {string} featureName
     * @param {import("./CADI-analyticsTypes").ClickInfo} [clickInfo]
     * @param {import("./CADI-analyticsTypes").FileInfo} [fileInfo]
     * @param {import("./CADI-analyticsTypes").TransactionInfo} [transactionInfo]
     * @param {import("./CADI-analyticsTypes").RoadblockInfo} [roadblockInfo]
     */
    trackAnalyticsClickEvent: (eventName, featureName, clickInfo, fileInfo, transactionInfo, roadblockInfo) => {}
};

/** @type {React.Context<import("./CADI-analyticsTypes").CADIAnalytics>} */
export const CADIAnalyticsContext = createContext(defaultAnalytics);

/**
 * Higher-order component that wraps a component with analytics context
 * @template P
 * @param {React.ComponentType<P & {analytics: import("./CADI-analyticsTypes").CADIAnalytics}>} Component - The component to wrap
 * @returns {React.ComponentType<P>} - The wrapped component
 */
export const withAnalytics = (Component) => {
    return (props) => (
        <CADIAnalyticsContext.Consumer>
            {(context) => <Component analytics={context} {...props} />}
        </CADIAnalyticsContext.Consumer>
    );
};

/**
 * Custom hook to use CADI analytics
 * @returns {import("./CADI-analyticsTypes").CADIAnalytics} CADI analytics methods
 */
export const useCADIAnalytics = () => {
    return useContext(CADIAnalyticsContext);
};

/**
 * CADI Analytics Provider component
 * @param {import("./CADI-analyticsTypes").CADIAnalyticsProviderProps} props - Component props
 * @returns {JSX.Element} - CADI Analytics Provider component
 */
export default (props) => {
    const logger = useLogger();
    const session = useSessionId();

    /**
     * Gets Investec ID from session storage
     * @returns {string} Investec ID
     */
    const getInvestecIdFromSessionStorage = () => {
        const oktaTokenStorageString = sessionStorage.getItem("okta-token-storage");

        if (!oktaTokenStorageString) {
            logger.info("No okta-token-storage found in session storage.");
            return "";
        }

        try {
            const {
                accessToken: {
                    claims: { investecId },
                },
            } = JSON.parse(oktaTokenStorageString);
            return investecId || "";
        } catch (error) {
            logger.error(
                "parsing okta-token-storage or retrieving investecId:",
                {},
                error
            );
            return "";
        }
    };

    const clientId = getInvestecIdFromSessionStorage();

    /**
     * Tracks an event
     * @param {AnalyticsBuilders.EventBuilder<any>} eventBuilder - Event builder
     * @param {import("./CADI-analyticsTypes").EventData} eventData - Event data
     * @param {string} [eventType='load'] - Event type
     */
    const trackEvent = (eventBuilder, eventData, eventType = 'load') => {
        if (!eventBuilder) return;

        let eventPayload;
        try {
            switch (eventType) {
                case 'load':
                    eventPayload = eventBuilder.buildLoadEvent({
                        eventInfo: eventData.eventInfo,
                    });
                    break;
                case 'click':
                    eventPayload = eventBuilder.buildClickEvent({
                        eventInfo: eventData.eventInfo,
                        clickInfo: eventData.clickInfo || {
                            targetURL: '',
                            productInfo: {
                                productName: '',
                                productID: ''
                            },
                            type: '',
                            action: ''
                        },
                        downloadInfo: eventData.downloadInfo || {
                            filePath: '',
                            fileType: ''
                        },
                        roadblockInfo: eventData.roadblockInfo
                    });
                    break;
                default:
                    console.error('Unsupported event type:', eventType);
                    return;
            }

            pushAnalytics(eventPayload);
        } catch (error) {
            console.error(`Error building ${eventType} event:`, error);
        }
    };

    /** @type {import("./CADI-analyticsTypes").UserInfo} */
    const userInfo = {
        userPersona: 'SaveUser',
        loggedIn: true,
        userID: clientId,
        deviceInfo: navigator.userAgent,
        sessionID: session.id
    };

    // Define the app info according to DigitalChannelType that EventBuilder expects
    const appInfo = {
        userPlatform: 'web', // EventBuilder expects specific platform values
        jurisdiction: 'uk',
        application: 'digital-savings',
        businessUnit: 'bfg',
    };

    // Create EventBuilder with proper typing
    const eventBuilder = new AnalyticsBuilders.EventBuilder(
        userInfo,
        window.raeEnv.envShort,
        /** @type {any} */ (appInfo) // Cast to any to avoid type mismatch with DigitalChannelType
    );

    /**
     * Tracks a page event
     * @param {string} eventName - Event name
     * @param {string} featureName - Feature name
     */
    const trackAnalyticsPageEvent = (eventName, featureName) => {
        /** @type {import("./CADI-analyticsTypes").EventData} */
        const eventData = {
            eventInfo: {
                eventName: eventName,
                featureName: featureName,
            }
        };

        trackEvent(eventBuilder, eventData);
    };

    /**
     * Tracks a click event
     * @param {string} eventName - Event name
     * @param {string} featureName - Feature name
     * @param {import("./CADI-analyticsTypes").ClickInfo} [clickInfo] - Click information
     * @param {import("./CADI-analyticsTypes").FileInfo} [fileInfo] - File information
     * @param {import("./CADI-analyticsTypes").TransactionInfo} [transactionInfo] - Transaction information
     * @param {import("./CADI-analyticsTypes").RoadblockInfo} [roadblockInfo] - Roadblock information
     */
    const trackAnalyticsClickEvent = (eventName, featureName, clickInfo, fileInfo, transactionInfo, roadblockInfo) => {
        /** @type {import("./CADI-analyticsTypes").EventData} */
        const eventData = {
            eventInfo: {
                eventName: eventName,
                featureName: featureName
            }
        };

        if (clickInfo) {
            eventData.clickInfo = {
                
                targetURL: clickInfo.url || '',
                productInfo: {
                    productName: clickInfo.productInfo?.productName || '',
                    productID: clickInfo.productInfo?.id || '', // Map id to productID
                    productTerm: clickInfo.productInfo?.productTerm || ''
                },
                // Only include properties that exist in IClickInfo
                // If region isn't in IClickInfo, you may need to omit it or map elsewhere
                type: clickInfo.type || '',
                action: clickInfo.action || ''
            };
            
            if (transactionInfo) {
                eventData.clickInfo.transactionType = transactionInfo.transactionType || '';
                eventData.clickInfo.transactionStage = transactionInfo.transactionStage || '';
                eventData.clickInfo.currency = transactionInfo.currency || '';
            }
        }

        if (fileInfo) {
            eventData.downloadInfo = {
                filePath: fileInfo.fileName || '',
                fileType: fileInfo.fileName
                    ? fileInfo.fileName.slice(((fileInfo.fileName.lastIndexOf(".") - 1) >>> 0) + 2)
                    : ''
            };
        }

        if (roadblockInfo && roadblockInfo.roadblockName) {
            eventData.roadblockInfo = {
                roadblockName: roadblockInfo.roadblockName
            };
        }

        trackEvent(eventBuilder, eventData, 'click');
    };

    /**
     * Pushes analytics data to Adobe Data Layer
     * @param {any} data - Analytics data
     */
    const pushAnalytics = (data) => {
        window.adobeDataLayer = window.adobeDataLayer || [];
        window.adobeDataLayer.push(data);
    };

    return (
        <CADIAnalyticsContext.Provider
            value={{
                trackAnalyticsPageEvent,
                trackAnalyticsClickEvent
            }}
        >
            {props.children}
        </CADIAnalyticsContext.Provider>
    );
};